import { useTranslate } from "#src/hooks";
import { SparkleIcon } from "@phosphor-icons/react";
import { Button, Flex, Form, Tooltip } from "antd";
import { useOrganization } from "hooks/useOrganization/";
import React, {
  forwardRef,
  SetStateAction,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import styled from "styled-components";
import { slugify } from "utils/text/";
import {
  MediaProjectResponse,
  ProjectInstanceSettingsRequest,
} from "../../types";
import { useSlugChecker } from "../hooks/useSlugChecker";
import { radius } from "@libertify/ui";

interface ProjectSlugSelectionProps {
  // Project/instance data may still be loading — the component already
  // reads both defensively (project?.title, media?.title etc.).
  project: MediaProjectResponse | undefined;
  media: ProjectInstanceSettingsRequest | undefined;
  // Plain setter (always called with a value, never an updater) so callers
  // can pass a setState for nullable state (string | null) directly.
  setSlug: (slug: string | null) => void;
  setAvailableSlug: React.Dispatch<SetStateAction<boolean>>;
  organizationId?: string;
}

export interface ProjectSlugSelectionRef {
  handleCheck: (
    slugOverride?: string
  ) => Promise<{ isAvailable: boolean; slug: string }>;
}

export const ProjectSlugSelection = forwardRef<
  ProjectSlugSelectionRef,
  ProjectSlugSelectionProps
>(({ project, media, setSlug, setAvailableSlug, organizationId }, ref) => {
  const t = useTranslate();
  const { getOrganization } = useOrganization();

  const organization = getOrganization(organizationId ?? "");
  const initialSlug = media?.instance_slug ?? undefined;
  const [isSlugModified, setIsSlugModified] = useState(false);

  const {
    form,
    slug: formSlug,
    availabilityResult,
    isLoading,
    isSlugAvailable,
    isSlugUnavailable,
    hasChecked,
    handleCheck,
    handleSlugChange,
    handleKeyPress,
    handleSelectAlternateSlug,
  } = useSlugChecker({
    checkEndpoint: (slug) =>
      `media/organizations/${organization?.slug}/instances/${slug}/availability`,
    initialSlug,
    initialValue: undefined,
    generateSlug: () => slugify(media?.title ?? project?.title ?? ""),
    onSlugCheck: (_checkedSlug, isAvailable) => {
      if (isAvailable) {
        setTimeout(() => {
          window.scrollTo({
            top: document.documentElement.scrollHeight,
            behavior: "smooth",
          });
        }, 50);
      }
    },
  });

  useImperativeHandle(ref, () => ({
    handleCheck,
  }));

  const lastInitializedSlugRef = useRef<string | null>(null);
  const lastSetSlugRef = useRef<string | null>(null);
  const lastSetAvailableSlugRef = useRef<boolean>(false);
  const isSyncingRef = useRef<boolean>(false);
  const hasInitializedRef = useRef<boolean>(false);

  const handleGenerateSlug = useCallback(() => {
    const newSlug = slugify(media?.title ?? project?.title ?? "");
    form.setFieldValue("slug", newSlug);
    void handleCheck(newSlug);
  }, [media, project?.title, form, handleCheck]);

  useEffect(() => {
    if (isSyncingRef.current || !hasInitializedRef.current) {
      return;
    }

    const currentSlug = formSlug || "";
    const isAvailable =
      (availabilityResult.available && availabilityResult.checked) ||
      currentSlug === initialSlug;

    let newSlug: string | null;
    if (currentSlug.trim().length === 0) {
      newSlug = "";
    } else if (isAvailable) {
      newSlug = currentSlug;
    } else {
      newSlug = null;
    }
    const newAvailableSlug = currentSlug.trim().length > 0 && isAvailable;

    if (lastSetSlugRef.current !== newSlug) {
      setSlug(newSlug);
      lastSetSlugRef.current = newSlug;
    }
    if (lastSetAvailableSlugRef.current !== newAvailableSlug) {
      setAvailableSlug(newAvailableSlug);
      lastSetAvailableSlugRef.current = newAvailableSlug;
    }
  }, [
    formSlug,
    initialSlug,
    availabilityResult.available,
    availabilityResult.checked,
    setSlug,
    setAvailableSlug,
  ]);

  const checkAndSetTitleAsSlug = useCallback(async () => {
    const slugifiedTitle = slugify(media?.title ?? project?.title ?? "");
    await handleCheck(slugifiedTitle);
    if (availabilityResult.available) {
      form.setFieldValue("slug", slugifiedTitle);
    }
  }, [
    media?.title,
    project?.title,
    handleCheck,
    availabilityResult.available,
    form,
  ]);

  useEffect(() => {
    if (
      initialSlug &&
      initialSlug.trim().length > 0 &&
      availabilityResult.checked &&
      availabilityResult.available
    ) {
      setSlug(initialSlug);
      lastSetSlugRef.current = initialSlug;
      setAvailableSlug(true);
      lastSetAvailableSlugRef.current = true;
    }
  }, [
    initialSlug,
    availabilityResult.checked,
    availabilityResult.available,
    setSlug,
    setAvailableSlug,
  ]);

  useEffect(() => {
    if (!initialSlug && !isSlugModified) {
      lastInitializedSlugRef.current = null;
      if (!hasInitializedRef.current) {
        hasInitializedRef.current = true;
      }
      checkAndSetTitleAsSlug().catch((error) => {
        console.error("Error checking and setting title as slug", error);
      });
      return;
    }

    const currentFormSlug = form.getFieldValue("slug") || "";
    const isGeneratedSlug = !media?.instance_slug;

    const shouldInitialize =
      (!hasInitializedRef.current && !currentFormSlug) ||
      (lastInitializedSlugRef.current !== initialSlug &&
        lastInitializedSlugRef.current !== null);

    if (shouldInitialize) {
      isSyncingRef.current = true;
      form.setFieldValue("slug", initialSlug);
      lastInitializedSlugRef.current = initialSlug ?? null;

      setTimeout(() => {
        isSyncingRef.current = false;
        hasInitializedRef.current = true;
      }, 0);

      if (
        isGeneratedSlug ||
        !availabilityResult.checked ||
        !availabilityResult.available
      ) {
        void handleCheck(initialSlug);
      }
    } else if (initialSlug === currentFormSlug && !availabilityResult.checked) {
      if (!hasInitializedRef.current) {
        hasInitializedRef.current = true;
      }
      void handleCheck(initialSlug);
    }
  }, [
    initialSlug,
    form,
    availabilityResult.checked,
    availabilityResult.available,
    handleCheck,
    media?.instance_slug,
    checkAndSetTitleAsSlug,
    isSlugModified,
    setSlug,
    setAvailableSlug,
  ]);

  return (
    <Flex vertical gap={2}>
      <Form form={form}>
        <UrlBuilderSM
          $hasChecked={hasChecked && formSlug.trim().length > 0}
          $isAvailable={isSlugAvailable && formSlug.trim().length > 0}
        >
          <SlashPrefix>... /</SlashPrefix>
          <Form.Item
            name="slug"
            noStyle
            normalize={(value: string) => value?.trimEnd()}
            rules={[
              {
                pattern: /^[a-zA-Z0-9-]+$/,
                message: t(
                  "media.projects.share.ChooseUrl.slugValidationMessage"
                ),
              },
            ]}
          >
            <UrlSlugInputSM
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                if (!initialSlug && !isSlugModified) {
                  setIsSlugModified(true);
                }
                setSlug(e.target.value);
                handleSlugChange(e);
              }}
              onKeyDown={handleKeyPress}
              onBlur={() => void handleCheck()}
              placeholder={t(
                "media.projects.share.ChooseUrl.pageNamePlaceholder"
              )}
              disabled={isLoading}
              maxLength={255}
            />
          </Form.Item>
          <UrlBuilderActions>
            <Tooltip title={t("components.UrlSlugChecker.generateSlug")}>
              <SlugAiBtn
                type="button"
                onClick={handleGenerateSlug}
                data-testid="generate-slug-button"
              >
                <SparkleIcon size={13} weight="fill" />
              </SlugAiBtn>
            </Tooltip>
          </UrlBuilderActions>
        </UrlBuilderSM>
      </Form>

      <SlugMetaRow>
        <div>
          {!formSlug && (
            <AvailabilityRowSM $ok={false}>
              <AvailDotSM $ok={false} />
              {t("components.UrlSlugChecker.slugEmpty")}
            </AvailabilityRowSM>
          )}
          {formSlug && isSlugUnavailable && (
            <AvailabilityRowSM $ok={false}>
              <AvailDotSM $ok={false} />
              {t("components.UrlSlugChecker.slugNotAvailable")}
            </AvailabilityRowSM>
          )}
          {isSlugAvailable && (
            <AvailabilityRowSM $ok={true}>
              <AvailDotSM $ok={true} />
              {t("components.UrlSlugChecker.slugAvailable")}
            </AvailabilityRowSM>
          )}
          {availabilityResult.alternate_slug && (
            <Button
              size="small"
              type="text"
              onClick={handleSelectAlternateSlug}
              icon={<SparkleIcon size={12} />}
              data-testid="suggest-alternate-slug"
              style={{ fontSize: 12 }}
            >
              {t("components.UrlSlugChecker.suggestAlternateSlug")}
            </Button>
          )}
        </div>
        <SlugCharCount>{formSlug?.length || 0}/255</SlugCharCount>
      </SlugMetaRow>
    </Flex>
  );
});

ProjectSlugSelection.displayName = "ProjectSlugSelection";

const UrlBuilderSM = styled.div<{
  $hasChecked: boolean;
  $isAvailable: boolean;
}>`
  display: flex;
  align-items: center;
  background: #f9fafb;
  border: 1.5px solid
    ${(p) =>
      p.$hasChecked ? (p.$isAvailable ? "#059669" : "#ef4444") : "#e5e7eb"};
  border-radius: 10px;
  overflow: hidden;
  margin-bottom: 8px;
  transition: border-color 0.15s;

  &:focus-within {
    border-color: ${(p) =>
      p.$hasChecked ? (p.$isAvailable ? "#059669" : "#ef4444") : "#14b8a6"};
  }
`;

const FONT_INTER = '"Inter Variable", "Inter", sans-serif';

const SlashPrefix = styled.span`
  flex-shrink: 0;
  padding-left: 12px;
  font-family: ${FONT_INTER};
  font-size: 13px;
  color: #9ca3af;
`;

const UrlSlugInputSM = styled.input`
  flex: 1;
  border: none;
  background: transparent;
  font-family: ${FONT_INTER};
  font-size: 13px;
  color: #111827;
  font-weight: 600;
  padding: 10px 6px;
  outline: none;
  min-width: 0;
`;

const UrlBuilderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  padding-right: 8px;
`;

const SlugCharCount = styled.span`
  font-family: ${FONT_INTER};
  font-size: 11px;
  color: #9ca3af;
  padding: 0 4px;
`;

const SlugAiBtn = styled.button`
  width: 28px;
  height: 28px;
  border-radius: 7px;
  background: #f0fdfb;
  border: 1.5px solid #ccfbf1;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: #14b8a6;
  transition: background 0.15s;

  &:hover {
    background: #ccfbf1;
  }
`;

const SlugMetaRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 20px;
`;

const AvailabilityRowSM = styled.div<{ $ok: boolean }>`
  display: flex;
  align-items: center;
  gap: 5px;
  font-family: ${FONT_INTER};
  font-size: 12px;
  color: ${(p) => (p.$ok ? "#059669" : "#ef4444")};
`;

const AvailDotSM = styled.span<{ $ok: boolean }>`
  width: 7px;
  height: 7px;
  border-radius: ${radius.circle};
  background: ${(p) => (p.$ok ? "#059669" : "#ef4444")};
  flex-shrink: 0;
`;
